import cacheKeys from '@debbie/common/dist/cache/keys.js';
import { availableScopes } from '@debbie/common/dist/keys/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as keysAPI from '@debbie/cortex/dist/api/keys.js';
import { keysEntity } from '@debbie/cortex/dist/keys/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenantId } from '@debbie/cortex/dist/tenant/index.js';
import { RiBarChartBoxLine, RiCheckLine, RiCheckboxCircleLine, RiCodeSSlashLine, RiDatabase2Line, RiDownloadLine, RiFileChartLine, RiKey2Line, RiQuestionLine, RiShieldKeyholeLine, RiShieldLine, } from '@remixicon/react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import powerbiwebconnectPNG from '../assets/images/powerbiwebconnect.png';
import { APIBASE } from '../config.js';
import { errorNotification } from '../utils/errors.js';
import Button from './Button.js';
import Checkbox from './Checkbox.js';
import CopyToClipboard from './CopyToClipboard.js';
import IconButton from './IconButton.js';
import Input from './Input.js';
import Error from './Messages/Error.js';
import Modal from './Modal.js';
import { dataSources, InsightSource, insightScope, } from './powerbi/data-sources.js';
import { Guide, GuideImage, GuideStep } from './powerbi/manual-guide.js';
import PowerBiKeys from './powerbi/PowerBiKeys.js';
import { downloadProject, insightUrl, powerQuery, projectArchive, projectFiles, } from './powerbi/project.js';
import { BrandIcon, Group, Header, Heading, HelpCard, Layout, Notice, Page, QueryPreview, SecurityNote, Selection, Sidebar, SourceCard, SourceCheckbox, SourceGrid, Success, SummaryBody, SummaryHeader, Toolbar, Action, ButtonContents, SourceActions, } from './powerbi/styles.js';
import { Tabs, Tab, TabList, TabPanel } from './Tabs.js';
import Well from './Well.js';
const ApiInsights = (props) => {
    const tenantId = useTenantId();
    return tenantId === undefined ? null : (React.createElement(Insights, { key: `${tenantId}-${props.creditorId ?? ''}`, ...props, tenantId: tenantId }));
};
const Insights = (props) => {
    const lang = useSelector((s) => s.lang);
    const currentLang = useSelector((s) => s.currentLang);
    const role = useRole();
    const labels = lang.insights.powerbi;
    const [selected, setSelected] = useState([]);
    const [name, setName] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [showKeys, setShowKeys] = useState(false);
    const [manualSource, setManualSource] = useState(null);
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const isClient = props.isClient === true || role === 'client';
    const scopes = role === null ? [] : (availableScopes[role] ?? []);
    const sourceScopes = availableScopes[isClient ? 'client' : 'admin'];
    const sources = dataSources.filter((source) => sourceScopes.includes(insightScope(source.id)));
    const connection = {
        apiBase: new URL(APIBASE, window.location.origin)
            .toString()
            .replace(/\/$/, ''),
        tenantId: props.tenantId,
        creditorId: props.creditorId,
        lang: currentLang,
    };
    const canCreate = scopes.length > 0 && (!isClient || props.creditorId !== undefined);
    const download = useMutation((file) => {
        downloadProject(projectArchive(projectFiles(lang, file.connection, file.sources, file.key)), `${file.name}.zip`);
    }, {
        onError: (error) => errorNotification(lang, error, 'any', labels.downloadProject),
    });
    const create = useMutation(async () => {
        const exportSources = selected.filter((source) => scopes.includes(insightScope(source.id)));
        if (!canCreate || !accepted || exportSources.length === 0) {
            throw new globalThis.Error(labels.selectSources);
        }
        const response = await keysAPI.create({
            name: name.trim(),
            scopes: exportSources.map((source) => insightScope(source.id)),
        }, props.creditorId);
        q.invalidate(cacheKeys.write.create(keysEntity));
        return {
            key: response.key,
            sources: exportSources,
            connection,
            name: `debbie-powerbi-${response.key.split(':')[0]}`,
        };
    }, {
        onSuccess: (file) => {
            if (!mounted.current) {
                return;
            }
            setConfirmationOpen(false);
            void download.mutate(file);
        },
        onError: (error) => {
            if (mounted.current) {
                errorNotification(lang, error, 'create', lang.errorEntities.apiKey);
            }
        },
    });
    const changeSources = (next) => {
        setSelected(next);
        setAccepted(false);
        create.reset();
        download.reset();
    };
    const groups = [
        {
            label: labels.groupCases,
            ids: [
                InsightSource.Cases,
                InsightSource.Creditors,
                InsightSource.Customers,
                InsightSource.UsersOnCustomers,
            ],
        },
        {
            label: labels.groupEconomy,
            ids: [
                InsightSource.CaseVouchers,
                InsightSource.BillingVouchers,
                InsightSource.PaymentPlans,
                InsightSource.PaymentPlanEntries,
            ],
        },
        {
            label: labels.groupActivity,
            ids: [
                InsightSource.CaseStatuses,
                InsightSource.Interactions,
                InsightSource.Documents,
                InsightSource.Satisfactions,
            ],
        },
        {
            label: labels.groupOrganisation,
            ids: [
                InsightSource.Users,
                InsightSource.Updates,
                InsightSource.UpdateTypes,
                InsightSource.Workflows,
                InsightSource.WorkflowTasks,
                InsightSource.Properties,
            ],
        },
    ];
    return (React.createElement(Page, null,
        React.createElement(Header, null,
            React.createElement(Heading, null,
                React.createElement(BrandIcon, null,
                    React.createElement(RiBarChartBoxLine, { size: 28 })),
                React.createElement("div", null,
                    React.createElement("h2", null, labels.title),
                    React.createElement("p", null, labels.shortIntro))),
            React.createElement(Button, { size: "small", theme: "secondary", onClick: () => setHelpOpen(true) },
                React.createElement(ButtonContents, null,
                    React.createElement(RiQuestionLine, { size: 17 }),
                    labels.help))),
        React.createElement(Tabs, { selectedIndex: showKeys ? 1 : 0, onSelect: (index) => setShowKeys(index === 1) },
            React.createElement(TabList, null,
                React.createElement(Tab, null,
                    React.createElement(ButtonContents, null,
                        React.createElement(RiDatabase2Line, { size: 17 }),
                        labels.dataSources)),
                canCreate && (React.createElement(Tab, null,
                    React.createElement(ButtonContents, null,
                        React.createElement(RiKey2Line, { size: 17 }),
                        lang.apiSettings.keys)))),
            React.createElement(TabPanel, null,
                download.isSuccess && (React.createElement(Success, null,
                    React.createElement("h3", null,
                        React.createElement(RiCheckboxCircleLine, { size: 22 }),
                        labels.projectReady),
                    React.createElement("p", null, labels.openInstructions),
                    React.createElement(Button, { size: "small", theme: "secondary", onClick: () => setShowKeys(true) }, labels.manageKeys))),
                React.createElement(Layout, null,
                    React.createElement("div", null,
                        React.createElement(Toolbar, null,
                            React.createElement("div", null,
                                React.createElement("h3", null, labels.chooseSources),
                                React.createElement("p", null, labels.chooseHint)),
                            React.createElement(Button, { size: "small", theme: "secondary", disabled: !canCreate || create.isLoading, onClick: () => changeSources(selected.length === sources.length ? [] : sources) }, selected.length === sources.length
                                ? labels.clearSelection
                                : labels.selectAll)),
                        !canCreate && React.createElement(Error, { messages: [labels.noAccess] }),
                        groups.map((group) => {
                            const groupSources = sources.filter((source) => group.ids.includes(source.id));
                            return groupSources.length === 0 ? null : (React.createElement(Group, { key: group.label },
                                React.createElement("h3", null, group.label),
                                React.createElement(SourceGrid, null, groupSources.map((source) => (React.createElement(SourceCard, { key: source.id, $selected: selected.some((item) => item.id === source.id) },
                                    React.createElement(SourceCheckbox, { checkMark: true, checked: selected.some((item) => item.id === source.id), disabled: !canCreate || create.isLoading, placeholder: React.createElement(React.Fragment, null,
                                            React.createElement("strong", null, lang.insights.apiTypes[source.id]),
                                            React.createElement("span", null, labels.sourceSummaries[source.id])), onChange: (event) => changeSources(event.target.checked
                                            ? [...selected, source]
                                            : selected.filter((item) => item.id !== source.id)) }),
                                    React.createElement(SourceActions, null,
                                        React.createElement(IconButton, { "aria-label": `${labels.manualConnection}: ${lang.insights.apiTypes[source.id]}`, title: labels.manualConnection, onClick: () => setManualSource(source) },
                                            React.createElement(RiCodeSSlashLine, { size: 17 })))))))));
                        }),
                        React.createElement(HelpCard, null,
                            React.createElement("summary", null, labels.extendTitle),
                            React.createElement("p", null, labels.extendHint))),
                    React.createElement(Sidebar, null,
                        React.createElement(SummaryHeader, null,
                            React.createElement(RiFileChartLine, { size: 27 }),
                            React.createElement("h3", null, labels.yourProject),
                            React.createElement("p", null, labels.projectContents)),
                        React.createElement(SummaryBody, null,
                            React.createElement(Selection, null,
                                React.createElement("strong", null,
                                    selected.length,
                                    " ",
                                    labels.selectedSources),
                                selected.length === 0 ? (React.createElement("p", null, labels.emptySelection)) : (React.createElement("ul", null, selected.map((source) => (React.createElement("li", { key: source.id },
                                    React.createElement(RiCheckLine, { size: 12 }),
                                    lang.insights.apiTypes[source.id])))))),
                            React.createElement(Action, null,
                                React.createElement(Input, { id: "powerbi-key-name", placeholder: lang.apiSettings.keyName, value: name, disabled: create.isLoading, onChange: (event) => {
                                        setName(event.target.value);
                                        create.reset();
                                        download.reset();
                                        setAccepted(false);
                                    } })),
                            React.createElement(Action, null,
                                React.createElement(Button, { block: true, disabled: !canCreate ||
                                        selected.length === 0 ||
                                        name.trim().length < 3, loading: create.isLoading || download.isLoading, onClick: () => {
                                        if (create.data !== null) {
                                            void download.mutate(create.data);
                                        }
                                        else {
                                            setConfirmationOpen(true);
                                        }
                                    } },
                                    React.createElement(ButtonContents, null,
                                        React.createElement(RiDownloadLine, { size: 17 }),
                                        create.data === null
                                            ? labels.downloadLabel
                                            : labels.downloadAgain))),
                            React.createElement("p", null, labels.downloadFormat)),
                        React.createElement(SecurityNote, null,
                            React.createElement(RiShieldKeyholeLine, { size: 16 }),
                            React.createElement("p", null, labels.shortSecurity))))),
            canCreate && (React.createElement(TabPanel, null,
                React.createElement(PowerBiKeys, { creditorId: props.creditorId, onRevoke: (id) => {
                        if (create.data?.key.split(':')[0] === id) {
                            create.reset();
                            download.reset();
                            setAccepted(false);
                        }
                    } })))),
        confirmationOpen && (React.createElement(Modal, { header: labels.confirmTitle, maxWidth: "560px", onToggle: () => {
                if (!create.isLoading) {
                    setConfirmationOpen(false);
                }
            } },
            React.createElement("p", null,
                selected.length,
                " ",
                labels.selectedSources),
            React.createElement(Notice, null,
                React.createElement(RiShieldLine, { size: 23 }),
                React.createElement("p", null, labels.securityWarning)),
            React.createElement(Checkbox, { checkMark: true, checked: accepted, disabled: create.isLoading, placeholder: labels.acceptWarning, onChange: (event) => setAccepted(event.target.checked) }),
            React.createElement(Button, { block: true, disabled: !accepted, loading: create.isLoading, onClick: () => {
                    void create.mutate();
                } },
                React.createElement(ButtonContents, null,
                    React.createElement(RiDownloadLine, { size: 17 }),
                    labels.downloadProject)))),
        helpOpen && (React.createElement(Modal, { header: labels.help, maxWidth: "650px", onToggle: () => setHelpOpen(false) },
            React.createElement(Guide, null,
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.chooseSources),
                    React.createElement("p", null, labels.intro)),
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.openProject),
                    React.createElement("p", null, labels.openInstructions)),
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.refreshTitle),
                    React.createElement("p", null, labels.refreshInstructions))))),
        manualSource !== null && (React.createElement(Modal, { header: `${labels.manualConnection}: ${lang.insights.apiTypes[manualSource.id]}`, maxWidth: "720px", onToggle: () => setManualSource(null) },
            React.createElement(Guide, null,
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.guideKeyTitle),
                    React.createElement("p", null, labels.guideKey),
                    React.createElement(Well, null,
                        React.createElement(CopyToClipboard, null, insightScope(manualSource.id)))),
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.guideWebTitle),
                    React.createElement("p", null, labels.guideWeb),
                    React.createElement(Well, null,
                        React.createElement(CopyToClipboard, null, insightUrl(connection, manualSource)))),
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.guideAuthTitle),
                    React.createElement("p", null, labels.guideAuth),
                    React.createElement(GuideImage, { src: powerbiwebconnectPNG, alt: labels.guideImageAlt })),
                React.createElement(GuideStep, null,
                    React.createElement("b", null, labels.guideLoadTitle),
                    React.createElement("p", null, labels.guideLoad))),
            React.createElement(HelpCard, null,
                React.createElement("summary", null, labels.advancedQuery),
                React.createElement("p", null, labels.manualKeyInstructions),
                React.createElement(CopyToClipboard, { text: powerQuery(connection, manualSource, 'PASTE_API_KEY_HERE') }, labels.copyQuery),
                React.createElement(QueryPreview, null, powerQuery(connection, manualSource, 'PASTE_API_KEY_HERE')))))));
};
export default ApiInsights;
//# sourceMappingURL=ApiInsights.js.map